import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { DateInput } from '@/components/ui/date-input'
import { Modal } from '@/components/ui/modal'
import { useToast } from '@/hooks/useToast'
import { tasksApi } from '@/lib/api'
import { extractApiError } from '@/lib/api'
import { convertToApiFormat, formatDateForInput } from '@/lib/utils'
import type { TaskCreationMode, TaskPalletRow } from '@/types'

interface TakePalletModalProps {
  taskUuid: string
  pallet: TaskPalletRow | null
  workerName: string
  creationMode?: TaskCreationMode
  onClose: () => void
  onTaken: (palletNum: number) => void
  takePallet: (args: {
    palletNum: number
    workerName: string
    production_date?: string
    expiry_date?: string
  }) => Promise<unknown>
}

export function TakePalletModal({
  taskUuid,
  pallet,
  workerName,
  creationMode,
  onClose,
  onTaken,
  takePallet,
}: TakePalletModalProps) {
  const { toast } = useToast()
  const [productionDate, setProductionDate] = useState('')
  const [expiryDate, setExpiryDate] = useState('')

  useEffect(() => {
    if (!pallet) return
    setProductionDate(formatDateForInput(pallet.production_date))
    setExpiryDate(formatDateForInput(pallet.expiry_date))
  }, [pallet])

  const isScanMode = creationMode === 'ka_file_only'

  const mutation = useMutation({
    mutationFn: async () => {
      if (!pallet) throw new Error('Паллета не выбрана')
      const prod = convertToApiFormat(productionDate)
      const exp = convertToApiFormat(expiryDate)
      if (!prod && !exp) {
        throw new Error('Укажите дату производства и/или срок годности')
      }
      if (isScanMode) {
        await takePallet({
          palletNum: pallet.num,
          workerName,
          ...(prod ? { production_date: prod } : {}),
          ...(exp ? { expiry_date: exp } : {}),
        })
        return
      }
      if (prod || exp) {
        await tasksApi.assignDates(taskUuid, {
          scope: 'pallet',
          num: pallet.num,
          ...(prod ? { production_date: prod } : {}),
          ...(exp ? { expiry_date: exp } : {}),
        })
      }
      await takePallet({ palletNum: pallet.num, workerName })
    },
    onSuccess: () => {
      if (pallet) onTaken(pallet.num)
      onClose()
    },
    onError: (err) => toast.error(extractApiError(err)),
  })

  if (!pallet) return null

  return (
    <Modal isOpen onClose={onClose}>
      <div className="px-6 py-4 border-b">
        <h3 className="text-lg font-semibold text-gray-900">
          Взять паллету №{pallet.num} в работу
        </h3>
      </div>

      <Modal.Body>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Дата производства
            </label>
            <DateInput
              value={productionDate}
              onChange={setProductionDate}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Срок годности
            </label>
            <DateInput
              value={expiryDate}
              onChange={setExpiryDate}
            />
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="ghost" onClick={onClose}>
          Отмена
        </Button>
        <Button onClick={() => mutation.mutate()} isLoading={mutation.isPending}>
          Начать работу
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
